import { DefaultExport } from '@admin/models/default-export.model';
import { BulkGroupAssignmentRequest, BulkOperationResponse } from '@admin/models/bulk-operations.model';
import {
  SaveTranslationProperties,
  TranslationAttributes,
  TranslationChange,
  TranslationData,
} from '@admin/models/translations.model';
import { ExportMetadata } from '@admin/pages/export-data/export-data.component';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpMethods } from '@cbre/http';
import { TabInfo, TabInfoRequest } from '@shared/models/entity-display.model';
import { Country, ICountryList, IOption, PlainOption } from '@shared/models/icommons.model';
import {
  CatalogList,
  IExportLimits,
  ILocationPermission,
  IRecordAccess,
  IUser,
  IUserRequest,
  UserGroups,
} from '@shared/models/iuser.model';
import {
  IM2MAppRequest,
  IM2MAppsResponse,
  IM2MAppCreateResponse,
  IM2MAppUpdateResponse,
} from '@shared/models/im2m-app.model';
import { LookupRefService } from '@shared/services/lookup-ref.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface NotifyChangesPayload {
  lobId: number;
  countryId: number;
}
@Injectable({ providedIn: 'root' })
export class AdminService extends HttpMethods {
  /*
   * /lookup/roles/
   * /lookup/usertypes/
   * /lookup/confidentiality/
   * /lookup/statuses/
   * /lookup/propertyusagetypes/
   * /geo/divisions/
   * /geo/globalregions
   * /geo/countries/
   * /geo/markets/
   * /geo/submarkets/
   * /geo/districts/
   * /geo/neighborhoods/
   * /lookup/confidentiality/
   * /lookup/statuses/
   */

  private countries: PlainOption[];

  constructor(
    http: HttpClient,
    private lookupService: LookupRefService
  ) {
    super(http);
  }

  /**
   * Request for an exisitng user, using an email as search
   * criteria.
   *
   * @param emailAddress email address used to search for a user.
   */
  public searchUserByEmail(emailAddress: string) {
    const uri = '/admin/users';
    const params = new HttpParams({ fromObject: { emailAddress } });
    return this.httpGet(uri, params);
  }

  /**
   * Get roles catalog.
   */
  public getRoles(): Observable<IOption[]> {
    return this.lookupService.getRoles();
  }

  /**
   * Get usertypes catalog.
   */
  public getUserTypes(): Observable<IOption[]> {
    return this.lookupService.getUserTypes();
  }

  /**
   * Get confidentiality catalog (numeric keys).
   */
  public getUserConfidentialityLevels(): Observable<IOption[]> {
    return this.lookupService.getUserConfidentialityLevels();
  }

  /**
   * Get user statuses catalog.
   */
  public getUserStatuses(): Observable<IOption[]> {
    return this.lookupService.getUserStatuses();
  }

  public getPermissionStatuses(): Observable<IOption[]> {
    return this.lookupService.getPermissionStatuses();
  }

  public getRecordTypes(): Observable<IOption[]> {
    return this.lookupService.dataClassesIds();
  }

  /**
   * Get Property Usage Types catalog.
   */
  public getPropertyUsageTypes(): Observable<IOption[]> {
    return this.lookupService.getPropertyUsageTypes();
  }

  public getUsers(): Observable<IUser[]> {
    const uri = '/users';
    return this.httpGet(uri);
  }

  public getLocationPermissionForUser(id: number): Observable<ILocationPermission[]> {
    const uri = `/users/${id}/perms/location`;
    return this.httpGet(uri);
  }

  public getRecordPermissionForUser(id: number): Observable<IRecordAccess[]> {
    const uri = `/users/${id}/perms/records`;
    return this.httpGet(uri);
  }

  /**
   * Get Regions catalog.
   */
  getDivisions(globalRegionId): Observable<IOption[]> {
    const uri = `/geo/divisions/${globalRegionId}`;
    return this.httpGet(uri);
  }

  /**
   * Get GlobalRegions catalog.
   */
  public getGlobalRegions(): Observable<IOption[]> {
    const uri = `/geo/globalregions`;
    return this.httpGet(uri);
  }

  /**
   * Get countries catalog.
   */
  getCountries(globalRegionId): Observable<IOption[]> {
    const uri = `/geo/countries/${globalRegionId}`;
    return this.httpGet(uri);
  }

  /**
   * Get markets catalog.
   */
  getMarkets(regionId): Observable<IOption[]> {
    const uri = `/geo/markets/${regionId}`;
    return this.httpGet(uri);
  }

  /**
   * Get submarkets catalog.
   */
  getSubMarkets(marketId): Observable<IOption[]> {
    const uri = `/geo/submarkets/${marketId}`;
    return this.httpGet(uri);
  }

  /**
   * Get districts catalog.
   */
  getDistricts(subMarketId): Observable<IOption[]> {
    const uri = `/geo/districts/${subMarketId}`;
    return this.httpGet(uri);
  }

  /**
   * Get Neighbourhoods catalog.
   */
  getNeighbourhoods(districtId): Observable<IOption[]> {
    const uri = `/geo/neighborhoods/${districtId}`;
    return this.httpGet(uri);
  }

  /**
   * Get confidentiality catalog (key as string).
   */
  getConfidentialityLevels(): Observable<IOption[]> {
    return this.lookupService.getConfidentialityLevels();
  }

  postNewUser(newUserRequest: IUserRequest): Observable<number> {
    const sendEmailNotif = newUserRequest.sendEmail ?? true;
    const uri = `/users/?sendEmail=${sendEmailNotif}`;
    return this.httpPost<IUserRequest>(uri, newUserRequest);
  }

  public updateUser(userReq: IUserRequest): Observable<number> {
    const sendEmailNotif = userReq.sendEmail ?? true;
    const uri = `/users/${userReq.user.id}?sendEmail=${sendEmailNotif}`;
    return this.httpPut(uri, userReq);
  }

  getExportLimits(userId): Observable<IExportLimits> {
    const uri = `/users/${userId}/exportlimits`;
    return this.httpGet(uri);
  }

  addPermissions(userId, postBody: ILocationPermission[]): Observable<any> {
    const uri = `/users/${userId}/perms/location`;
    return this.httpPost<ILocationPermission[]>(uri, postBody);
  }

  deletePermissions(userId, permissionId): Observable<any> {
    const uri = `/users/${userId}/perms/location/${permissionId}`;
    return this.httpDelete(uri, permissionId);
  }

  addRecordPermissions(userId: number, postBody: IRecordAccess[]): Observable<any> {
    const uri = `/users/${userId}/perms/records`;
    return this.httpPost<IRecordAccess[]>(uri, postBody);
  }

  deleteRecordPermission(userId: number, permissionId: number): Observable<any> {
    const uri = `/users/${userId}/perms/records/${permissionId}`;
    return this.httpDelete(uri, permissionId);
  }

  cloneUser(userEmail, type?: string): Observable<any> {
    let uri = `/users/clone?email=${userEmail}`;
    if (type && type !== 'both') {
      uri = `${uri}&type=${type}`;
    }
    return this.httpGet(uri);
  }

  public keywordSearch(keyword): Observable<any[]> {
    const uri = `/users/search?query=${keyword}`;
    return this.httpGet(uri);
  }

  public userSearch(keyword): Observable<IUser[]> {
    const uri = `/users?query=${keyword}`;
    return this.httpGet(uri);
  }

  public userGroups(): Observable<UserGroups[]> {
    const uri = `/lookup/groups/catalog`;
    return this.httpGet(uri);
  }

  /**
   * Add groups to multiple users in bulk with batch processing
   * Processes users in batches to prevent API timeout
   * @param userEmails Array of user email addresses
   * @param groupKeys Array of group key IDs to add
   * @param updateBy Email of the admin performing the update
   * @param batchSize Number of users per batch (default: 10)
   * @returns Observable with aggregated results from all batches
   */
  public addGroupsToUsers(
    userEmails: string[],
    groupKeys: number[],
    updateBy: string,
    batchSize = 10
  ): Observable<BulkOperationResponse> {
    if (userEmails.length === 0) {
      return this.createEmptyResponse();
    }

    const batches = this.createBatches(userEmails, batchSize);
    const batchRequests = batches.map(batch => this.addGroupsToUsersBatch(batch, groupKeys, updateBy));

    return forkJoin(batchRequests).pipe(map(responses => this.aggregateResponses(responses)));
  }

  /**
   * Create empty response for when no users are provided
   */
  private createEmptyResponse(): Observable<BulkOperationResponse> {
    return of({
      message: 'No users to process',
      success: [],
      error: [],
    });
  }

  /**
   * Split array into batches of specified size
   */
  private createBatches(items: string[], batchSize: number): string[][] {
    if (batchSize <= 0) {
      // set default batch size to 1 if invalid
      batchSize = 1;
    }
    const batches: string[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    return batches;
  }

  /**
   * Aggregate results from multiple batch responses
   */
  private aggregateResponses(responses: BulkOperationResponse[]): BulkOperationResponse {
    const aggregatedResponse: BulkOperationResponse = {
      message: '',
      success: [],
      error: [],
    };

    responses.forEach(response => {
      aggregatedResponse.success.push(...response.success);
      aggregatedResponse.error.push(...response.error);
    });

    aggregatedResponse.message = this.generateResponseMessage(
      aggregatedResponse.success.length,
      aggregatedResponse.error.length
    );

    return aggregatedResponse;
  }

  /**
   * Generate appropriate message based on success/error counts
   */
  private generateResponseMessage(successCount: number, errorCount: number): string {
    if (errorCount === 0) {
      return 'Groups successfully added to all users';
    }
    if (successCount === 0) {
      return 'Failed to add groups to all users';
    }
    return `Groups partially added. ${successCount} succeeded, ${errorCount} failed.`;
  }

  /**
   * Add groups to a single batch of users
   * @param userEmails Batch of user email addresses
   * @param groupKeys Array of group key IDs to add
   * @param updateBy Email of the admin performing the update
   * @returns Observable with batch operation response
   */
  private addGroupsToUsersBatch(
    userEmails: string[],
    groupKeys: number[],
    updateBy: string
  ): Observable<BulkOperationResponse> {
    const uri = '/users/groups';
    const payload: BulkGroupAssignmentRequest = {
      userEmails,
      groupKeys,
      updateBy,
    };
    return this.httpPost<BulkGroupAssignmentRequest>(uri, payload);
  }

  public getUserCatalogs(): Observable<CatalogList[]> {
    const uri = `/catalog`;
    return this.httpGet(uri);
  }

  public getExportData(email: string): Observable<ExportMetadata[]> {
    const uri = '/export';
    const params = new HttpParams({ fromObject: { email } });
    return this.httpGet(uri, params);
  }

  /**
   * @deprecated use `saveTranslationChanges` instead
   */
  public saveAttributesTranslation(data: TranslationData[], id, countryCode): Observable<SaveTranslationProperties[]> {
    const uri = `/translation/attributes?country=${countryCode}&language=${id}`;
    return this.httpPut(uri, data);
  }

  /**
   * @deprecated use `saveTranslationChanges` instead
   */
  public saveSectionsTranslation(data: TranslationData[], id, countryCode): Observable<SaveTranslationProperties[]> {
    const uri = `/translation/sections?country=${countryCode}&language=${id}`;
    return this.httpPut(uri, data);
  }

  /**
   * Unifies Section and Attributes changes
   * @param data TranslationChange
   * @returns Observable<SaveTranslationProperties[]>
   */
  public saveTranslationChanges(data: TranslationChange): Observable<SaveTranslationProperties[]> {
    return this.httpPut('/translation', data);
  }

  public getCountryLanguageList(): Observable<ICountryList[]> {
    const uri = `/translation/countrylanguage`;
    return this.httpGet(uri);
  }

  public getCountryCatalogList(countryid): Observable<any[]> {
    const uri = `/catalog?country=${countryid}`;
    return this.httpGet(uri);
  }

  public getTransalationAttributes(languageid, dataclass, countryid, lobId): Observable<TranslationAttributes[]> {
    // tslint:disable-next-line:max-line-length
    const uri = `/translation/attributes?language=${languageid}&dataClass=${dataclass}&country=${countryid}&lobId=${lobId}`;
    return this.httpGet(uri);
  }

  public getDefaultData(): Observable<DefaultExport[]> {
    const uri = '/users/defaultexport';
    return this.httpGet(uri);
  }

  public getCountryList(): Observable<PlainOption[]> {
    if (this.countries) {
      return of(this.countries);
    }
    const uri = '/lookup/catalog/country';
    return this.httpGet(uri).pipe(tap(result => (this.countries = result)));
  }

  public getLobList(countryId): Observable<any[]> {
    const uri = `/lookup/catalog/lobs?country=${countryId}`;
    return this.httpGet(uri);
  }
  public getCatalogsList(): Observable<CatalogList[]> {
    const uri = '/catalog';
    return this.httpGet(uri);
  }

  public getCountryLobData(countryId?: number, lobId?: number): Observable<Country[]> {
    const uri = '/catalog/countrylobs' + (countryId ? '?country=' + countryId : '') + (lobId ? '&lobId=' + lobId : '');
    return this.httpGet(uri);
  }

  public createCatalog(countryId, lobId): Observable<any[]> {
    const uri = `/catalog/checkCountryLob?country=${countryId}&lobId=${lobId}`;
    return this.httpGet(uri);
  }

  public getCatalogAttributes(countryId?: number, lobId?: number): Observable<any[]> {
    const uri = `/catalog/catalogattrs?country=${countryId}&lobId=${lobId}`;
    return this.httpGet(uri);
  }

  public createCatalogAttributes(data: any): Observable<any> {
    const uri = '/catalog/catalogattrs';
    return this.httpPost(uri, data);
  }

  public refreshColumnRef(query?: string, method?: string) {
    const params = this.getHttpParams(query, method);
    query = params.toString();
    const finalQuery = query ? `?${query}` : '';
    const url = `/lookup/columns${finalQuery}`;
    return this.lookupService.refreshColumnRef(url, true);
  }

  private getHttpParams(query?: string, method?: string) {
    let params = new HttpParams();
    if (!!query) {
      params = params.set('query', query);
    }
    if (!!method) {
      params = params.set('method', method);
    }
    return params;
  }

  /**
   * @deprecated use `updateEntityTabsSubTabs` instead
   */
  public setEntitTabsSubTabs(data: TabInfo[], userCountrySkey: number, userLobSkey: number): Observable<TabInfo[]> {
    const uri = `/catalog/displayoption?country=${userCountrySkey}&lobId=${userLobSkey}`;
    return this.httpPut(uri, data);
  }

  public updateEntityTabsSubTabs(data: TabInfoRequest): Observable<TabInfo[]> {
    return this.httpPut('/catalog/displayoption/v2', data);
  }

  public catalogNotifyChanges(requestPayload: NotifyChangesPayload) {
    return this.httpPost(
      `/catalog/notifyChanges?lobId=${requestPayload.lobId}&countryId=${requestPayload.countryId}`,
      {}
    );
  }

  // ============================================
  // M2M Application Management API Methods
  // ============================================

  /**
   * Get all M2M applications with their configurations and virtual user settings
   * @returns Observable with apps array and total count
   */
  public getM2MApps(): Observable<IM2MAppsResponse> {
    const uri = '/m2m/admin/apps';
    return this.httpGet(uri);
  }

  /**
   * Create a new M2M application with an associated virtual user
   * @param appRequest M2M application request with app_config and user_config
   * @returns Observable with virtual_user_skey, app_permission_skey, and message
   */
  public createM2MApp(appRequest: IM2MAppRequest): Observable<IM2MAppCreateResponse> {
    const uri = '/m2m/admin/apps';
    return this.httpPost(uri, appRequest);
  }

  /**
   * Update an existing M2M application (uses PATCH semantics - only provided fields are updated)
   * @param appPermissionSkey The app_permission_skey of the M2M application
   * @param appRequest Partial M2M application request with fields to update
   * @returns Observable with virtual_user_skey, app_permission_skey, and message
   */
  public updateM2MApp(
    appPermissionSkey: number,
    appRequest: Partial<IM2MAppRequest>
  ): Observable<IM2MAppUpdateResponse> {
    const uri = `/m2m/admin/apps/${appPermissionSkey}`;
    return this.httpPut(uri, appRequest);
  }
}
